import { v4 as uuid } from "uuid";
import { getWidgetSize } from "@pages/CanvasEditor/Elements/utils";
import { ELEMENT_ID } from "./element-id";
import { CanvaElement, Preset } from "./canva.types";
import { FEATURE_TOGGLES, featureToggleManager } from "@lib/featureToggles";
import { useAllFeatureToggles } from "@hooks/useFeatureToggle";
import { useMemo } from "react";
import salesPreview from "@assets/img/presets/sales.png";
import goalPreview from "@assets/img/presets/goal.png";
import launchPreview from "@assets/img/presets/launch.png";
import relaunchPreview from "@assets/img/presets/prelaunch.png";
import cwoPreview from "@assets/img/presets/cwo.png";

const getFeatureKeyForElement = (elementId: ELEMENT_ID): string => {
  const elementToFeatureMap: Record<ELEMENT_ID, string> = {
    [ELEMENT_ID.SUMMARY_AUDIT]: "CANVA_ELEMENT_SUMMARY_AUDIT",
    [ELEMENT_ID.EXECUTIVE_SUMMARY]: "CANVA_ELEMENT_EXECUTIVE_SUMMARY",
    [ELEMENT_ID.SALES_PERFORMANCE]: "CANVA_ELEMENT_SALES_PERFORMANCE",
    [ELEMENT_ID.SALES_CHANNEL]: "CANVA_ELEMENT_SALES_CHANNEL",
    [ELEMENT_ID.CUSTOMER_JOURNEY_MAP]: "CANVA_ELEMENT_CUSTOMER_JOURNEY_MAP",
    [ELEMENT_ID.PRODUCT_DETAILS]: "CANVA_ELEMENT_PRODUCT_DETAILS",
    [ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS]:
      "CANVA_ELEMENT_EXTERNAL_PRODUCT_DETAILS",
    [ELEMENT_ID.SCORE_REVIEWS]: "CANVA_ELEMENT_SCORE_REVIEWS",
    [ELEMENT_ID.ASPECT_OPTIMIZATION]: "CANVA_ELEMENT_ASPECT_OPTIMIZATION",
    [ELEMENT_ID.CONTENT_OPTIMIZATION]: "CANVA_ELEMENT_CONTENT_OPTIMIZATION",
    [ELEMENT_ID.PRICE_OPTIMIZATION]: "CANVA_ELEMENT_PRICE_OPTIMIZATION",
    [ELEMENT_ID.REVIEWS_OPTIMIZATION]: "CANVA_ELEMENT_REVIEWS_OPTIMIZATION",
    [ELEMENT_ID.ADS_SALES_SLIDERS]: "CANVA_ELEMENT_ADS_SALES_SLIDERS",
    [ELEMENT_ID.SATURATION_CURVE]: "CANVA_ELEMENT_SATURATION_CURVE",
    [ELEMENT_ID.GOAL_SETTING]: "CANVA_ELEMENT_GOAL_SETTING",
    [ELEMENT_ID.ADS_SANKEY]: "CANVA_ELEMENT_ADS_SANKEY",
    [ELEMENT_ID.FUNNEL]: "CANVA_ELEMENT_FUNNEL",
    [ELEMENT_ID.PRODUCT_PLACEMENT]: "CANVA_ELEMENT_PRODUCT_PLACEMENT",
    [ELEMENT_ID.ADS_SALES]: "CANVA_ELEMENT_ADS_SALES",
    [ELEMENT_ID.CAMPAIGN_METRICS]: "CANVA_ELEMENT_CAMPAIGN_METRICS",
    [ELEMENT_ID.ORGANIC_SALES]: "CANVA_ELEMENT_ORGANIC_SALES",
    [ELEMENT_ID.ORGANIC_KEYWORD_PERFORMANCE]:
      "CANVA_ELEMENT_ORGANIC_KEYWORD_PERFORMANCE",
    [ELEMENT_ID.ORGANIC_MISSING_TARGET]: "CANVA_ELEMENT_ORGANIC_MISSING_TARGET",
    [ELEMENT_ID.FINANCIAL_OVERVIEW]: "CANVA_ELEMENT_FINANCIAL_OVERVIEW",
    [ELEMENT_ID.CASH_FLOW]: "CANVA_ELEMENT_CASH_FLOW",
    [ELEMENT_ID.WATERFALL]: "CANVA_ELEMENT_WATERFALL",
    [ELEMENT_ID.INVENTORY_PERFORMANCE]: "CANVA_ELEMENT_INVENTORY_PERFORMANCE",
    [ELEMENT_ID.INVENTORY_OVERVIEW]: "CANVA_ELEMENT_INVENTORY_OVERVIEW",
    [ELEMENT_ID.MARKET_LEVEL_SALES_DISTRIBUTION]:
      "CANVA_ELEMENT_MARKET_LEVEL_SALES_DISTRIBUTION",
    [ELEMENT_ID.MARKET_OVERVIEW]: "CANVA_ELEMENT_MARKET_OVERVIEW",
    [ELEMENT_ID.MARKET_SHARE]: "CANVA_ELEMENT_MARKET_SHARE",
    [ELEMENT_ID.MARKET_SALES_PERFORMANCE]:
      "CANVA_ELEMENT_MARKET_SALES_PERFORMANCE",
    [ELEMENT_ID.BRAND_SALES_PERFORMANCE]:
      "CANVA_ELEMENT_BRAND_SALES_PERFORMANCE",
    [ELEMENT_ID.LAUNCH_PRODUCT]: "CANVA_ELEMENT_LAUNCH_PRODUCT",
    [ELEMENT_ID.UNDEFINED]: "CANVA_ELEMENT_UNDEFINED",
    [ELEMENT_ID.WHAT_IF_CHART]: "CANVA_ELEMENT_WHAT_IF_CHART",
    [ELEMENT_ID.DEMOGRAPHIC_TYPE]: "CANVA_ELEMENT_DEMOGRAPHIC_TYPE",
    [ELEMENT_ID.DEMOGRAPHIC_TABLE]: "CANVA_ELEMENT_DEMOGRAPHIC_TABLE",
    [ELEMENT_ID.DEMOGRAPHIC_TYPE_SHARE]: "CANVA_ELEMENT_DEMOGRAPHIC_TYPE_SHARE",
    [ELEMENT_ID.BRAND]: "CANVA_ELEMENT_BRAND",
    [ELEMENT_ID.PNL_BLOCK]: "CANVA_ELEMENT_PNL_BLOCK",
    [ELEMENT_ID.TARGITING_PROFILES]: "CANVA_ELEMENT_TARGITING_PROFILES",
    [ELEMENT_ID.DATA_ENTRY]: "CANVA_ELEMENT_DATA_ENTRY",
    // Add fallback for any missing elements
    [ELEMENT_ID.LAUNCH_PRODUCT_TABLE]: "CANVA_ELEMENT_LAUNCH_PRODUCT",
    [ELEMENT_ID.FUNNEL_MARKET]: "CANVA_ELEMENT_FUNNEL",
    [ELEMENT_ID.TOTAL_SALES]: "CANVA_ELEMENT_SALES_PERFORMANCE",
    [ELEMENT_ID.CONTRIBUTION_PROFIT]: "CANVA_ELEMENT_FINANCIAL_OVERVIEW",
    [ELEMENT_ID.MARKET_OVERVIEW_BUBBLE]: "CANVA_ELEMENT_MARKET_OVERVIEW",
    [ELEMENT_ID.SALES_VELICITY]: "CANVA_ELEMENT_SALES_PERFORMANCE",
    [ELEMENT_ID.PRODUCT_COST]: "CANVA_ELEMENT_FINANCIAL_OVERVIEW",
    [ELEMENT_ID.DATA_ENTRY_TABLE]: "CANVA_ELEMENT_DATA_ENTRY",
  };

  return elementToFeatureMap[elementId] || "CANVA_ELEMENT_UNDEFINED";
};

const isElementEnabled = (elementId: ELEMENT_ID): boolean => {
  const featureKey = getFeatureKeyForElement(elementId);
  return featureToggleManager.isFeatureEnabled(featureKey as any);
};

const filterElementsByFeatureFlags = (
  elements: CanvaElement[],
): CanvaElement[] => {
  return elements.filter((element) => isElementEnabled(element.elementId));
};

export const defaultElements: CanvaElement[] = [
  {
    id: uuid(),
    elementId: ELEMENT_ID.SUMMARY_AUDIT,
    x: 0,
    y: 14,
    size: getWidgetSize(ELEMENT_ID.SUMMARY_AUDIT),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
    x: 0,
    y: 50,
    size: getWidgetSize(ELEMENT_ID.EXECUTIVE_SUMMARY),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ADS_SALES,
    x: 8,
    y: 14,
    size: getWidgetSize(ELEMENT_ID.ADS_SALES),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ORGANIC_SALES,
    x: 8,
    y: 50,
    size: getWidgetSize(ELEMENT_ID.ORGANIC_SALES),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ADS_SANKEY,
    x: 16,
    y: 14,
    size: getWidgetSize(ELEMENT_ID.ADS_SANKEY),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.FUNNEL,
    x: 16,
    y: 50,
    size: getWidgetSize(ELEMENT_ID.FUNNEL),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS,
    x: 24,
    y: 14,
    size: getWidgetSize(ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.PRODUCT_DETAILS,
    x: 26,
    y: 14,
    size: getWidgetSize(ELEMENT_ID.PRODUCT_DETAILS),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.CONTENT_OPTIMIZATION,
    x: 31,
    y: 14,
    size: getWidgetSize(ELEMENT_ID.CONTENT_OPTIMIZATION),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.PRICE_OPTIMIZATION,
    x: 31,
    y: 8,
    size: getWidgetSize(ELEMENT_ID.PRICE_OPTIMIZATION),
    active: true,
  },
  {
    id: uuid(),
    elementId: ELEMENT_ID.ASPECT_OPTIMIZATION,
    x: 31,
    y: 16,
    size: getWidgetSize(ELEMENT_ID.ASPECT_OPTIMIZATION),
    active: true,
  },
];

// Create a hook for reactive preset filtering
export const useFilteredPresets = (): Preset[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return presets.map((preset) => ({
      ...preset,
      elements: preset.elements.filter((element) => {
        const featureKey = getFeatureKeyForElement(element.elementId);
        const stringKey =
          FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key;
        return allFeatureStates[stringKey] ?? false;
      }),
    }));
  }, [allFeatureStates]);
};

// Create a hook for reactive default elements
export const useFilteredDefaultElements = (): CanvaElement[] => {
  const allFeatureStates = useAllFeatureToggles();

  return useMemo(() => {
    return defaultElements.filter((element) => {
      const featureKey = getFeatureKeyForElement(element.elementId);
      const stringKey =
        FEATURE_TOGGLES[featureKey as keyof typeof FEATURE_TOGGLES]?.key;
      return allFeatureStates[stringKey] ?? false;
    });
  }, [allFeatureStates]);
};

// Export function to get filtered presets (keep for backward compatibility)
export const getFilteredPresets = (): Preset[] => {
  return presets.map(filterPresetElements);
};

// Export function to get filtered default elements (keep for backward compatibility)
export const getFilteredDefaultElements = (): CanvaElement[] => {
  return filterElementsByFeatureFlags(defaultElements);
};

// Filter presets to only include enabled elements
const filterPresetElements = (preset: Preset): Preset => ({
  ...preset,
  elements: filterElementsByFeatureFlags(preset.elements),
});

export const presets: Preset[] = [
  {
    name: "Sales Performance",
    previewImage: salesPreview,
    elements: [
      {
        id: uuid(),
        elementId: ELEMENT_ID.SUMMARY_AUDIT,
        x: 0,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.SUMMARY_AUDIT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SALES_PERFORMANCE,
        x: 0,
        y: 61,
        size: getWidgetSize(ELEMENT_ID.SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.FUNNEL,
        x: 0,
        y: 116,
        size: getWidgetSize(ELEMENT_ID.FUNNEL),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
        x: 8,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.EXECUTIVE_SUMMARY),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ADS_SALES,
        x: 8,
        y: 61,
        size: getWidgetSize(ELEMENT_ID.ADS_SALES),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ADS_SALES_SLIDERS,
        x: 8,
        y: 130,
        size: getWidgetSize(ELEMENT_ID.ADS_SALES_SLIDERS),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ORGANIC_SALES,
        x: 8,
        y: 181,
        size: getWidgetSize(ELEMENT_ID.ORGANIC_SALES),
        active: true,
      },
    ],
  },
  {
    name: "Goal Setting",
    previewImage: goalPreview,
    elements: [
      {
        id: uuid(),
        elementId: ELEMENT_ID.SUMMARY_AUDIT,
        x: 0,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.SUMMARY_AUDIT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SALES_PERFORMANCE,
        x: 0,
        y: 61,
        size: getWidgetSize(ELEMENT_ID.SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.FUNNEL,
        x: 0,
        y: 116,
        size: getWidgetSize(ELEMENT_ID.FUNNEL),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ORGANIC_SALES,
        x: 0,
        y: 192,
        size: getWidgetSize(ELEMENT_ID.ORGANIC_SALES),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
        x: 8,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.EXECUTIVE_SUMMARY),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ADS_SALES_SLIDERS,
        x: 8,
        y: 56,
        size: getWidgetSize(ELEMENT_ID.ADS_SALES_SLIDERS),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.GOAL_SETTING,
        x: 13,
        y: 56,
        size: getWidgetSize(ELEMENT_ID.GOAL_SETTING),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ADS_SALES,
        x: 16,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.ADS_SALES),
        active: true,
      },
    ],
  },
  {
    name: "Product Launch",
    previewImage: launchPreview,
    elements: [
      {
        id: uuid(),
        elementId: ELEMENT_ID.SUMMARY_AUDIT,
        x: 0,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.SUMMARY_AUDIT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.MARKET_SALES_PERFORMANCE,
        x: 0,
        y: 61,
        size: getWidgetSize(ELEMENT_ID.MARKET_SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.BRAND_SALES_PERFORMANCE,
        x: 0,
        y: 116,
        size: getWidgetSize(ELEMENT_ID.BRAND_SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
        x: 8,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.EXECUTIVE_SUMMARY),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.LAUNCH_PRODUCT,
        x: 8,
        y: 56,
        size: getWidgetSize(ELEMENT_ID.LAUNCH_PRODUCT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.UNDEFINED,
        x: 16,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.UNDEFINED),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.MARKET_LEVEL_SALES_DISTRIBUTION,
        x: 16,
        y: 56,
        size: getWidgetSize(ELEMENT_ID.MARKET_LEVEL_SALES_DISTRIBUTION),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.MARKET_OVERVIEW,
        x: 16,
        y: 130,
        size: getWidgetSize(ELEMENT_ID.MARKET_OVERVIEW),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.MARKET_SHARE,
        x: 20,
        y: 130,
        size: getWidgetSize(ELEMENT_ID.MARKET_SHARE),
        active: true,
      },
    ],
  },
  {
    name: "Product Relaunch",
    previewImage: relaunchPreview,
    elements: [
      {
        id: uuid(),
        elementId: ELEMENT_ID.SUMMARY_AUDIT,
        x: 0,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.SUMMARY_AUDIT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.MARKET_SALES_PERFORMANCE,
        x: 0,
        y: 61,
        size: getWidgetSize(ELEMENT_ID.MARKET_SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.BRAND_SALES_PERFORMANCE,
        x: 0,
        y: 116,
        size: getWidgetSize(ELEMENT_ID.BRAND_SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
        x: 8,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.EXECUTIVE_SUMMARY),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.LAUNCH_PRODUCT,
        x: 8,
        y: 56,
        size: getWidgetSize(ELEMENT_ID.LAUNCH_PRODUCT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.UNDEFINED,
        x: 16,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.UNDEFINED),
        active: true,
      },
    ],
  },
  {
    name: "CMO Workflow Optimization",
    previewImage: cwoPreview,
    elements: [
      {
        id: uuid(),
        elementId: ELEMENT_ID.SUMMARY_AUDIT,
        x: 0,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.SUMMARY_AUDIT),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SALES_PERFORMANCE,
        x: 0,
        y: 61,
        size: getWidgetSize(ELEMENT_ID.SALES_PERFORMANCE),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.FUNNEL,
        x: 0,
        y: 116,
        size: getWidgetSize(ELEMENT_ID.FUNNEL),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXECUTIVE_SUMMARY,
        x: 8,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.EXECUTIVE_SUMMARY),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ADS_SALES,
        x: 8,
        y: 56,
        size: { width: 8, height: 50 },
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.SATURATION_CURVE,
        x: 8,
        y: 107,
        size: { width: 5, height: 50 },
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ADS_SALES_SLIDERS,
        x: 13,
        y: 107,
        size: { width: 3, height: 50 },
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.PRODUCT_DETAILS,
        x: 16,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.PRODUCT_DETAILS),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.ASPECT_OPTIMIZATION,
        x: 16,
        y: 110,
        size: getWidgetSize(ELEMENT_ID.ASPECT_OPTIMIZATION),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.CONTENT_OPTIMIZATION,
        x: 18,
        y: 110,
        size: getWidgetSize(ELEMENT_ID.CONTENT_OPTIMIZATION),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.PRICE_OPTIMIZATION,
        x: 20,
        y: 110,
        size: getWidgetSize(ELEMENT_ID.PRICE_OPTIMIZATION),
        active: true,
      },
      {
        id: uuid(),
        elementId: ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS,
        x: 21,
        y: 14,
        size: getWidgetSize(ELEMENT_ID.EXTERNAL_PRODUCT_DETAILS),
        active: true,
      },
    ],
  },
];
